import {useCallback, useMemo} from 'react';
import {
    SortableContainer as createSortableContainer,
    SortableElement as createSortableElement,
} from 'react-sortable-hoc';

import {Text} from '../../../../../../component/text/text';
import {ExportColumnCatalogItemType} from '../../../../../../service/reviews/export-constructor/export-constructor-type';
import {arrayMove} from '../../../../../../util/array';

import {ExportTemplatePreviewLabel} from './export-template-preview-label';
import * as styles from './export-template-preview-table.module.scss';

const EXPORT_TEMPLATE_PREVIEW_TABLE_DRAG_HELPER_CLASS = 'export-template-preview-table-drag-helper';

type PropsType = {
    selectedColumnIds: Array<string>;
    catalog: Array<ExportColumnCatalogItemType>;
    onReorder: (columnIds: Array<string>) => void;
};

type PreviewColumnType = {
    columnId: string;
    label: string;
};

type SortableHeaderCellPropsType = {
    label: string;
};

const SortableHeaderCell = createSortableElement<SortableHeaderCellPropsType>(
    (cellProps: SortableHeaderCellPropsType) => {
        const {label} = cellProps;

        return (
            <div className={styles.ExportTemplatePreviewTable_headerCell}>
                <Text
                    block
                    bolder
                    className={styles.ExportTemplatePreviewTable_headerLabel}
                    ellipsis={{tooltip: true}}
                    small
                >
                    {label}
                </Text>
            </div>
        );
    }
);

const SortableHeaderRow = createSortableContainer<{items: Array<PreviewColumnType>}>(
    (rowProps: {items: Array<PreviewColumnType>}) => {
        const {items} = rowProps;

        return (
            <div className={styles.ExportTemplatePreviewTable_headerRow}>
                {items.map((item, index) => (
                    <SortableHeaderCell index={index} key={item.columnId} label={item.label} />
                ))}
            </div>
        );
    }
);

export function ExportTemplatePreviewTable(props: PropsType): JSX.Element {
    const {selectedColumnIds, catalog, onReorder} = props;

    const catalogMap = useMemo(() => {
        return catalog.reduce<Record<string, string>>((accumulator, column) => {
            return {...accumulator, [column.id]: column.label};
        }, {});
    }, [catalog]);

    const items = selectedColumnIds.map((columnId) => ({
        columnId,
        label: catalogMap[columnId] ?? columnId,
    }));

    const handleSortEnd = useCallback(
        (sortData: {oldIndex: number; newIndex: number}) => {
            onReorder(arrayMove([...selectedColumnIds], sortData.oldIndex, sortData.newIndex));
        },
        [onReorder, selectedColumnIds]
    );

    if (items.length === 0) {
        return (
            <div className={styles.ExportTemplatePreviewTable}>
                <ExportTemplatePreviewLabel />
                <Text
                    className={styles.ExportTemplatePreviewTable_empty}
                    secondary
                    stringKey="REVIEWS__EXPORT_CONSTRUCTOR__COLUMNS_REQUIRED"
                />
            </div>
        );
    }

    return (
        <div className={styles.ExportTemplatePreviewTable}>
            <ExportTemplatePreviewLabel />
            <div className={styles.ExportTemplatePreviewTable_scroll}>
                <div className={styles.ExportTemplatePreviewTable_table}>
                    <SortableHeaderRow
                        axis="x"
                        helperClass={EXPORT_TEMPLATE_PREVIEW_TABLE_DRAG_HELPER_CLASS}
                        items={items}
                        lockAxis="x"
                        onSortEnd={handleSortEnd}
                    />
                    <div className={styles.ExportTemplatePreviewTable_dataRow}>
                        {items.map((item) => (
                            <div className={styles.ExportTemplatePreviewTable_dataCell} key={item.columnId} />
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}
